import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type {
  SPDChapterCommitteePosition,
  LocalChapterCommitteePosition,
} from '../types';

// Union type for position objects
export type Position =
  | SPDChapterCommitteePosition
  | LocalChapterCommitteePosition;

export interface PositionSelectorProps {
  value?: string | string[] | Position | Position[]; // flexible value types
  onChange: (value: string | string[] | null) => void;
  positions: Position[];
  multiple?: boolean;
  label?: string;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
  disabled?: boolean;
  placeholder?: string;
}

const PositionSelector: React.FC<PositionSelectorProps> = ({
  value, // single ID, array of IDs, or position objects
  onChange, // callback with selected value(s)
  positions, // array of position options { id, name }
  multiple = false, // single vs multi-select mode
  label = 'Position',
  size = 'small',
  variant = 'standard',
  disabled = false,
  placeholder,
}) => {
  // Convert value to appropriate format for Autocomplete
  const getAutocompleteValue = () => {
    if (!value) return multiple ? [] : null;

    if (multiple) {
      // For multi-select, value can be array of IDs or objects
      if (Array.isArray(value)) {
        if (value.length === 0) return [];
        // If first item has 'id' property, assume objects
        if (typeof value[0] === 'object' && 'id' in value[0])
          return value as Position[];
        // Otherwise assume array of IDs
        return positions.filter((pos) => (value as string[]).includes(pos.id));
      }
      return [];
    } else {
      // For single-select, value can be ID or object
      if (typeof value === 'object' && value !== null && 'id' in value)
        return value as Position;
      return positions.find((pos) => pos.id === value) || null;
    }
  };

  const handleChange = (event: any, newValue: Position | Position[] | null) => {
    if (multiple) {
      // Return array of IDs for multi-select
      const selectedIds = newValue
        ? (newValue as Position[]).map((pos) => pos.id)
        : [];
      onChange(selectedIds);
    } else {
      // Return single ID for single-select
      onChange(newValue ? (newValue as Position).id : null);
    }
  };

  return (
    <Autocomplete<Position, boolean, boolean, boolean>
      multiple={multiple}
      size={size}
      disabled={disabled}
      options={positions || []}
      value={getAutocompleteValue()}
      onChange={handleChange}
      getOptionLabel={(option) => (option as Position)?.name || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
        />
      )}
    />
  );
};

export default PositionSelector;
